import axios, { AxiosRequestConfig, isAxiosError } from 'axios';

import useBoundStore from 'store';
import { API_BASE_URL } from './constants';

export type CustomAxiosReqConfig = AxiosRequestConfig & { errorToast?: boolean };
export type F<T> = { result?: undefined; error: string } | { result: T; error?: undefined };

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

const fetcher = <T = any>(url: string, config?: CustomAxiosReqConfig) =>
  axiosInstance<never, T & { error?: string }>(url, config).then((result): F<T> => {
    if (!result.error) {
      return { result };
    }

    const error = typeof result.error === 'string' ? result.error : 'Неизвестная ошибка';

    if (config?.errorToast) {
      useBoundStore.getState().showToast({ message: error });
    }

    return result as { error: string };
  });

const createMethodFunc =
  (method: string) =>
  <T>(url: string, data?: any, config?: CustomAxiosReqConfig) =>
    fetcher<T>(url, { ...config, data, method });

fetcher.get = createMethodFunc('get')
fetcher.post = createMethodFunc('post');
fetcher.patch = createMethodFunc('patch');
fetcher.delete = createMethodFunc('delete');

const swrFetcher = <T = any>(url: string, config?: CustomAxiosReqConfig) =>
  axiosInstance<never, T & { error?: string }>(url, config).then((res) => {
    if (res === undefined) {
      throw 'Ошибка соединения с сервером';
    }

    if (res.error === 'Unauthorized') {
      useBoundStore.getState().showToast({ message: 'Вы не авторизованы' });
    }

    if (res.error) {
      throw res.error;
    }

    return res as T;
  });

fetcher.swr = swrFetcher;

axiosInstance.interceptors.request.use((config) => {
  const token = useBoundStore.getState().authToken;

  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res.data,
  (err) => {
    if (isAxiosError(err)) {
      const data = err.response?.data;
      if (typeof data === 'object' && data.message) {
        if (data.message === 'Unauthorized' || Number(data.statusCode) === 401) {
          return { error: 'Вы не авторизованы' };
        }
        return { error: String(data.message) };
      }
    }
  },
);

export default fetcher;
